import { CDK_TABLE, CdkFooterRowDef, CdkHeaderRowDef, CdkRowDef } from '@angular/cdk/table';
import { Directive, Input, IterableDiffers, TemplateRef, forwardRef, inject } from '@angular/core';

/**
 * @module ApTableModule
 *
 * @ngdoc directive
 * @name ApHeaderRowDefDirective
 *
 * @description
 * Header row definition for the ap-table.
 * Captures the header row's template and other header properties such as the columns to display.
 *
 */
@Directive({
    selector: '[apHeaderRowDef]',
    providers: [{ provide: CdkHeaderRowDef, useExisting: ApHeaderRowDefDirective }]
})
export class ApHeaderRowDefDirective extends CdkHeaderRowDef {
    _table? = inject(CDK_TABLE, { optional: true });

    @Input() set apHeaderRowDef(value: Iterable<string>) {
        this.columns = value;
    }

    constructor() {
        super(inject<TemplateRef<any>>(TemplateRef), inject(IterableDiffers));
    }
}

/**
 * @module ApTableModule
 *
 * @ngdoc directive
 * @name ApFooterRowDefDirective
 *
 * @description
 * Footer row definition for the ap-table.
 * Captures the footer row's template and other header properties such as the columns to display.
 *
 */
@Directive({
    selector: '[apFooterRowDef]',
    providers: [{ provide: CdkFooterRowDef, useExisting: ApFooterRowDefDirective }]
})
export class ApFooterRowDefDirective extends CdkFooterRowDef {
    @Input() set apFooterRowDef(value: Iterable<string>) {
        this.columns = value;
    }

    constructor() {
        const template = inject(TemplateRef<any>);
        const _differs = inject(IterableDiffers);
        const _table = inject(
            forwardRef(() => CDK_TABLE),
            { optional: true }
        );
        super(template, _differs, _table);
    }
}

/**
 * @module ApTableModule
 *
 * @ngdoc directive
 * @name ApRowDefDirective
 *
 * @description
 * Data row definition for the ap-table.
 * Captures the header row's template and other row properties such as the columns to display and
 * a when predicate that describes when this row should be used.
 *
 */
@Directive({
    selector: '[apRowDef]',
    providers: [{ provide: CdkRowDef, useExisting: ApRowDefDirective }]
})
export class ApRowDefDirective<T> extends CdkRowDef<T> {
    @Input() set apRowDefColumns(value: Iterable<string>) {
        this.columns = value;
    }
    @Input() set apRowDefWhen(value: (index: number, rowData: T) => boolean) {
        this.when = value;
    }
    constructor() {
        super(inject<TemplateRef<any>>(TemplateRef), inject(IterableDiffers));
    }
}
